/**
 * Firebase Firestore - Unified Database Access
 * 
 * This module provides a proxy-based Firestore accessor that handles async initialization gracefully.
 * It works with firebase-loader.ts to ensure Firestore is properly initialized before use.
 */

import { 
  getFirestoreInstance, 
  getFirestoreInstanceAsync, 
  isFirebaseInitialized, 
  waitForFirebase 
} from './config';
import type { Firestore } from 'firebase/firestore';

const handler: ProxyHandler<Firestore> = {
  get(_target, prop, receiver) {
    // If Firebase is initialized, use the instance
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        const value = Reflect.get(instance as any, prop, receiver);
        return typeof value === 'function' ? value.bind(instance) : value;
      } catch {
        // Fall through to error
      }
    }
    
    throw new Error(`Firestore not initialized. Property '${String(prop)}' accessed before initialization. Ensure initializeFirebase() was called in main.ts.`);
  },
  
  set(_target, prop, value) {
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        (instance as any)[prop] = value;
        return true;
      } catch {
        // Fall through
      }
    }
    return true;
  },
  
  has(_target, prop) {
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        return prop in instance;
      } catch {
        // Fall through
      }
    }
    return false;
  },
  
  ownKeys() {
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        return Reflect.ownKeys(instance as any);
      } catch {
        // Fall through
      }
    }
    return [];
  },
  
  getOwnPropertyDescriptor(_target, prop) {
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        return Object.getOwnPropertyDescriptor(instance as any, prop);
      } catch {
        // Fall through
      }
    }
    return undefined;
  },
  
  getPrototypeOf() {
    if (isFirebaseInitialized()) {
      try {
        const instance = getFirestoreInstance();
        return Object.getPrototypeOf(instance);
      } catch {
        // Fall through
      }
    }
    return Object.prototype;
  }
};

export const DB = new Proxy({} as Firestore, handler) as Firestore;

/**
 * Get Firestore instance synchronously (throws if not initialized)
 * @deprecated Use getDbInstanceAsync() instead for guaranteed initialization
 */
export function getDbInstance(): Firestore {
  return getFirestoreInstance();
}

/**
 * Get Firestore instance asynchronously (waits for initialization)
 */
export async function getDbInstanceAsync(): Promise<Firestore> {
  return getFirestoreInstanceAsync();
}
